/**
 * Riga della coda ticket V3 (prototipo `Centro Ticket.dc.html` L141-L159).
 *
 * Struttura: rail priorita 4px + tre livelli.
 *  1. pallino non letto · oggetto (700 se non letto) · chip SLA · eta
 *  2. #numero · nominativo del richiedente
 *  3. stato · valutazione · priorita · origine · tipo richiedente · canale ·
 *     tag · "Nuovo messaggio" · avatar +N
 *
 * Parita con `TicketQueue.tsx:669-742`: stessi contenuti, stesse etichette,
 * stesso tetto di 3 avatar. In piu diventano VISIBILI origine e tag, che oggi
 * sono filtrabili ma non mostrati (rilievo §11.3 dell'inventario).
 *
 * E un `<button>` nativo: click, Enter e Spazio arrivano gratis da lui.
 */
import React from 'react';
import { S3Avatar, S3Badge } from './primitives';
import type { S3SlaBadge } from './visual';
import type { QueueRowModel } from './queueModel';
import { useSupportLook } from '../v4/lookContext';
import { channelTone } from '../v4/channelPalette';
import ChannelSwatch from '../v4/ChannelSwatch';
import ChannelAvatarMark from '../ChannelAvatarMark';
import { hasChannelAvatar } from '../channelAvatar';
import StatusGlyph from '../v4/StatusGlyph';
import DerivedStatusMark from '../DerivedStatusMark';
import PriorityBars from '../v4/PriorityBars';
import { DraftsBadge, V4Avatars, V4Rating } from '../v4/QueueRowParts';

export interface QueueRowProps {
  row: QueueRowModel;
  /** Esito di `slaBadge(classifySla(ticket, new Date()))`; `null` su stato terminale o senza SLA. */
  sla: S3SlaBadge | null;
  onOpen: (id: string) => void;
}

/** Chip neutro (canale e tag): stesso trattamento del prototipo L156. */
const CHIP: React.CSSProperties = {
  font: "500 9.5px 'DM Sans', sans-serif",
  color: 'var(--a2-mut)',
  background: 'var(--a2-chip)',
  padding: '2px 7px',
  borderRadius: 999,
  flex: 'none',
};

const QueueRowV3: React.FC<QueueRowProps> = ({ row, sla, onOpen }) => (
  <button
    type="button"
    className={`s3-row${row.selected ? ' s3-row--on' : ''}`}
    aria-current={row.selected ? 'true' : undefined}
    onClick={() => onOpen(row.id)}
  >
    <span className="s3-rail" style={{ background: row.railColor }} />

    <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* livello 1 */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {row.unread && <span className="s3-dot-unread" aria-label="Messaggio non letto" />}
        {row.unreadNote && <span className="s3-dot-note" aria-label="Nota interna da leggere" />}
        <span
          className="s3-ellipsis s3-row__subj"
          style={{ font: `${row.subjectWeight} 12.5px 'DM Sans', sans-serif`, color: 'var(--a2-tx)', flex: 1 }}
        >
          {row.subject}
        </span>
        {sla && (
          <S3Badge tone={sla.tone} small title={sla.title}>
            {sla.label}
          </S3Badge>
        )}
        <span className="s3-meta" style={{ flex: 'none' }}>
          {row.age}
        </span>
      </span>

      {/* livello 2 */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
        <span className="s3-meta" style={{ flex: 'none' }}>
          #{row.number}
        </span>
        <span
          className="s3-ellipsis s3-row__who"
          style={{ font: "500 11px 'DM Sans', sans-serif", color: 'var(--a2-mut)' }}
        >
          {row.requesterName}
        </span>
      </span>

      {/* livello 3 */}
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {row.statusDisplay.derived ? (
          <DerivedStatusMark d={row.statusDisplay} size={11} />
        ) : (
          <S3Badge tone={row.status.tone} small>
            {row.status.label}
          </S3Badge>
        )}
        {/* Valutazione accanto allo stato: compare solo a ticket valutato (ruling 18). */}
        {row.rating !== null && (
          <span className="s3-meta" style={{ flex: 'none' }} title={`Valutazione ${row.rating}/5`}>
            <span aria-hidden="true">★ {row.rating}</span>
            <span className="s3-sr">Valutazione {row.rating} su 5</span>
          </span>
        )}
        <S3Badge tone={row.priority.tone} small>
          {row.priority.label}
        </S3Badge>
        {row.origin.label && (
          <S3Badge tone={row.origin.tone} small>
            {row.origin.label}
          </S3Badge>
        )}
        {row.requesterType.label && (
          <S3Badge tone={row.requesterType.tone} small>
            {row.requesterType.label}
          </S3Badge>
        )}
        {row.groupName && (hasChannelAvatar(row.channel) ? (
          <span style={{ ...CHIP, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ChannelAvatarMark channel={row.channel} size={14} />
            <span>{row.groupName}</span>
          </span>
        ) : <span style={CHIP}>{row.groupName}</span>)}
        {row.tags.map((t) => (
          <span key={t} style={CHIP}>
            #{t}
          </span>
        ))}
        {row.unread && (
          <S3Badge tone="green" small>
            Nuovo messaggio
          </S3Badge>
        )}
        {row.unreadNote && (
          <S3Badge tone="warn" small>
            Nota da leggere
          </S3Badge>
        )}
        <DraftsBadge count={row.pendingDrafts} />
        {(row.assigneeInitials.length > 0 || row.extraAssignees > 0) && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 3, alignItems: 'center' }}>
            {row.assigneeInitials.map((ini, i) => (
              <S3Avatar
                key={row.assigneeKeys[i]}
                initials={ini}
                size={20}
                title={row.assigneeNames[i]}
                src={row.assigneePhotos[i]}
              />
            ))}
            {row.extraAssignees > 0 && <span className="s3-meta">+{row.extraAssignees}</span>}
          </span>
        )}
      </span>
    </span>
  </button>
);

/**
 * Veste v4 della riga a card (L249-L270): bordo sinistro col colore del canale,
 * glifo di stato, barre di priorita, nome del canale colorato. Tutte le
 * informazioni della v3 restano (SLA col tooltip, origine, tipo richiedente,
 * tag, valutazione, badge dei non letti, avatar con foto).
 */
const QueueRowV4: React.FC<QueueRowProps> = ({ row, sla, onOpen }) => {
  const tone = channelTone(row.channel);
  return (
    <button
      type="button"
      className={`s3-row s4-qcard${row.selected ? ' s3-row--on' : ''}${row.active ? '' : ' s4-row--off'}`}
      style={{ ['--s4-rail' as string]: `var(${tone.cssVar})`, ['--s4-chfg' as string]: `var(${tone.cssVar}-fg)` } as React.CSSProperties}
      aria-current={row.selected ? 'true' : undefined}
      onClick={() => onOpen(row.id)}
    >
      <span className="s4-qcard__l1">
        {row.unread && <span className="s3-dot-unread" aria-label="Messaggio non letto" />}
        {row.unreadNote && <span className="s3-dot-note s4-rhomb" aria-label="Nota interna da leggere" />}
        <span
          className="s3-ellipsis s3-row__subj"
          style={{ font: `${row.subjectWeight} 13px 'DM Sans', sans-serif`, color: 'var(--a2-tx)', flex: 1 }}
          title={row.tagsTitle}
        >
          {row.subject}
        </span>
        {sla && (
          <S3Badge tone={sla.tone} small title={sla.title}>
            {sla.label}
          </S3Badge>
        )}
        <span className="s3-meta" style={{ flex: 'none' }}>
          {row.age}
        </span>
      </span>

      <span className="s4-qcard__l2">
        <span className="s3-meta" style={{ flex: 'none' }}>
          #{row.number}
        </span>
        <span className="s3-ellipsis s3-row__who">{row.requesterName}</span>
        {row.requesterType.label && (
          <S3Badge tone={row.requesterType.tone} small>
            {row.requesterType.label}
          </S3Badge>
        )}
      </span>

      <span className="s4-qcard__l3">
        {row.statusDisplay.derived ? (
          <DerivedStatusMark d={row.statusDisplay} size={11} />
        ) : (
          <StatusGlyph status={row.statusKey} size={10} withLabel />
        )}
        <PriorityBars priority={row.priorityKey} withLabel />
        {row.groupName && hasChannelAvatar(row.channel) && <ChannelSwatch channel={row.channel} size={14} />}
        {row.groupName && <span className="s4-chname">{row.groupName}</span>}
        {row.origin.label && (
          <S3Badge tone={row.origin.tone} small>
            {row.origin.label}
          </S3Badge>
        )}
        {row.rating !== null && <V4Rating stars={row.rating} />}
        {row.tags.map((t) => (
          <span key={t} className="s4-tag">
            #{t}
          </span>
        ))}
        {row.unread && (
          <S3Badge tone="green" small>
            Nuovo messaggio
          </S3Badge>
        )}
        {row.unreadNote && (
          <S3Badge tone="warn" small>
            Nota da leggere
          </S3Badge>
        )}
        <DraftsBadge count={row.pendingDrafts} />
        {(row.assigneeInitials.length > 0 || row.extraAssignees > 0) && (
          <span style={{ marginLeft: 'auto' }}>
            <V4Avatars row={row} size={22} />
          </span>
        )}
      </span>
    </button>
  );
};

/** Veste scelta dal contesto (`useSupportLook`): con 'v3' la riga di sempre. */
const QueueRow: React.FC<QueueRowProps> = (props) =>
  useSupportLook() === 'v4' ? <QueueRowV4 {...props} /> : <QueueRowV3 {...props} />;

export default QueueRow;
